import { PaymentStatus } from "../../operations"
import { SaleOrder } from "../state/types"
import { Params } from "../../../state/reducers/types"
import { ListResponse, cutOffBeforeDate } from "../../common"
import { useRoadmapLoaders } from "../../logistics"
import { useManagerHooks } from "../../manager"
import { useCustomerLoaders } from "../loaders/customerLoaders"
import { SaleFilters } from "./types"
import { useSaleLoaders } from "../loaders/saleLoaders"
import { DataType } from "../../../tables"
import { Data2 } from "../../../features/data-types"
import { isUninvoicedFiscalDocumentStatus } from "../../fiscal-documents"
import { DeliveryStatus, useDeliveryLoaders } from "../../deliveries"
import { ValueFilter } from "../../../services/dynamo"

export const useSaleHooks = () => {
    const { currentBranchId } = useManagerHooks()

    const saleLoaders = useSaleLoaders()
    const customerLoaders = useCustomerLoaders()
    const deliveryLoaders = useDeliveryLoaders()
    const roadmapLoaders = useRoadmapLoaders()

    const loadSalesBatch = async (
        filtersData: Data2 = {},
        batchSize?: number,
        startKey?: Params
    ): Promise<ListResponse<SaleOrder>> => {
        const { searchText, customerId, sellerId, dateFrom, dateTo, paymentTypeId, paymentStatus, deliveryStatus, latePayment, saleStatus } = filtersData as SaleFilters
        
        const sort = {
            index: 'CodeIndex',
            prop: 'code',
            asc: false
        }
        const textFilters = searchText?.trim() ?
            [{ prop: 'codeStr', text: searchText.trim() }] :
            undefined

        const valueFilters: ValueFilter[] = [
            { prop: 'branchId', value: currentBranchId! },
            { prop: 'archived', value: true, equals: false }
        ]
        customerId && valueFilters.push({ prop: 'customerId', value: customerId })
        sellerId && valueFilters.push({ prop: 'sellerId', value: sellerId })
        paymentTypeId && valueFilters.push({ prop: 'paymentTypeId', value: paymentTypeId })
        paymentStatus && valueFilters.push({ prop: 'paymentStatus', value: paymentStatus })
        deliveryStatus && valueFilters.push({ prop: 'deliveryStatus', value: deliveryStatus })
        latePayment && valueFilters.push({ prop: 'latePayment', value: latePayment === 'true', equals: true })
        if (!saleStatus || saleStatus === 'active') {
            valueFilters.push({ prop: 'cancelled', value: true, equals: false })
        } else if (saleStatus === 'cancelled') {
            valueFilters.push({ prop: 'cancelled', value: true, equals: true })
        }

        const dateTimeFrom = dateFrom
        dateTimeFrom?.setHours(0, 0, 0, 0)
        const dateTimeTo = dateTo
        dateTimeTo?.setHours(23, 59, 59, 999)
        // "Desde" no va a DynamoDB: como filtro posterior obligaría a recorrer toda la tabla. Se aplica con corte temprano.
        const rangeFilters = dateTimeTo ?
            [{
                prop: 'creationDate',
                end: dateTimeTo.toISOString()
            }] :
            undefined

        const response = await saleLoaders().loadSalesBatch({
            startKey,
            limit: batchSize,
            sort,
            textFilters,
            valueFilters,
            rangeFilters
        })
        return cutOffBeforeDate(response, dateTimeFrom)
    }

    const loadBatchData = async (salesDT: DataType[]) => {
        const sales = salesDT as SaleOrder[]
        const { loadCustomers } = customerLoaders()
        await loadCustomers(sales.map(sale => sale.customerId))
        const { loadOperationsDeliveries } = deliveryLoaders()
        const deliveries = await loadOperationsDeliveries(sales.map(sale => sale.id))
        const { loadDeliveriesRoadmapsLinks } = roadmapLoaders()
        await loadDeliveriesRoadmapsLinks(deliveries.map(delivery => delivery.id))
    }

    return {
        loadSalesBatch,
        loadBatchData
    }
}

export const saleHelpers = {
    isDeliveryEditable: (sale: SaleOrder): boolean => {
        const { deliveryStatus, cancelled, archived } = sale
        return deliveryStatus === DeliveryStatus.PENDING && !cancelled && !archived
    },

    isInvoiceable: (sale: SaleOrder): boolean => {
        const { invoiceStatus, cancelled, archived } = sale
        return isUninvoicedFiscalDocumentStatus(invoiceStatus) && !cancelled && !archived
    },

    isArchivable: (sale: SaleOrder): boolean => {
        const { paymentStatus, deliveryStatus, cancelled, archived } = sale
        return paymentStatus === PaymentStatus.PAYED && deliveryStatus === DeliveryStatus.DELIVERED && !cancelled && !archived
    }
}
